import { repairImageKitUrl } from '@dupe/common/constants'
import Link from 'next/link'
import type { PublishedResearchReportIndexItem } from '~/src/db/queryPublishedResearchReport'
import {
  getReportCategoryLabel,
  getReportSubcategoryLabel,
} from '~/src/lib/reports/categoryLabels'
import { cn } from '~/src/lib/utils'

// Pinned to UTC: these cards are ISR-rendered server-side (UTC) and then
// hydrated client-side — a viewer-local timezone would both shift calendar
// dates near midnight and cause a React hydration mismatch on the text.
const REPORT_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
})

function formatUpdatedDate(dateStr: string): string | null {
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return null
  return `Updated ${REPORT_DATE_FORMATTER.format(date)}`
}

function CategoryBadge({ children }: { children: string }) {
  // Plain span (not a link) because the enclosing card is already a Link to
  // the report, and nested anchors break accessibility + Next routing.
  // Category hub navigation lives in the "Browse by category" strip above
  // the grid.
  return (
    <span className='rounded-lg bg-gray-50 px-2 py-0.5 text-sm font-medium text-gray-700'>
      {children}
    </span>
  )
}

interface ReportListCardProps {
  report: PublishedResearchReportIndexItem
  variant: 'large' | 'standard'
  /**
   * The /reports index card shows category/subcategory badges; the category
   * and subcategory list pages omit them (the page itself is the category
   * context). Consolidates the three identical pages-router copies.
   */
  showCategoryBadges?: boolean
}

export default function ReportListCard({
  report,
  variant,
  showCategoryBadges = false,
}: ReportListCardProps): React.ReactElement {
  const imageHeight = variant === 'large' ? 'h-[300px]' : 'h-[240px]'
  const titleSize =
    variant === 'large' ? 'text-[30px] leading-[38px]' : 'text-2xl leading-8'

  return (
    <Link
      href={report.path}
      className='group flex flex-1 flex-col gap-3 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
    >
      <div
        className={cn(
          'relative w-full overflow-hidden rounded-2xl bg-gray-100',
          imageHeight
        )}
      >
        {report.heroImageUrl ? (
          <img
            src={repairImageKitUrl(report.heroImageUrl)}
            alt={report.title}
            loading={variant === 'large' ? 'eager' : 'lazy'}
            className='h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]'
          />
        ) : (
          <div className='flex h-full items-center justify-center text-sm text-gray-500'>
            No image
          </div>
        )}
      </div>
      <div className='flex flex-col gap-2'>
        {showCategoryBadges && report.categorySlug && (
          <div className='flex gap-2'>
            <CategoryBadge>
              {getReportCategoryLabel(report.categorySlug)}
            </CategoryBadge>
            {report.subcategorySlug && (
              <CategoryBadge>
                {getReportSubcategoryLabel(
                  report.categorySlug,
                  report.subcategorySlug
                )}
              </CategoryBadge>
            )}
          </div>
        )}
        <h3
          className={cn(
            'text-balance font-display font-semibold tracking-tight text-gray-900',
            titleSize
          )}
        >
          {report.title}
        </h3>
        <p className='text-base text-gray-500'>
          {report.updatedAt
            ? formatUpdatedDate(report.updatedAt)
            : report.publishedAt
              ? formatUpdatedDate(report.publishedAt)
              : null}
        </p>
      </div>
    </Link>
  )
}
